import {
  createFetch as createZapierFetch,
  FetchInit,
  FetchOptions,
  responseBody,
} from '@zapier/transfer-fetch';

// Methods
import getSession from './methods/getSession';
import logout from './methods/logout';
import submitBatch from './methods/submitBatch';
import getZapReadRecords from './methods/getZapReadRecords';
import getFilteredRecords from './methods/getFilteredRecords';
import getPublicTriggerApps from './methods/getPublicTriggerApps';
import getPrivateTriggerApps from './methods/getPrivateTriggerApps';
import getSchedule from './methods/getSchedule';
import createOrUpdateSchedule from './methods/createOrUpdateSchedule';
import getEntitlementsImplementation from './methods/getEntitlements';

import throwForStatus from '../../middleware/throwForStatus';
import addParams from '../shared/utils/addParams';
import createErrorHandler from '../shared/utils/createErrorHandler';
import { wsClient } from './utils/wsClient';

import { Params } from '../shared/types';

export const createClient = (options?: FetchOptions) => {
  const zapierFetch = createZapierFetch({
    afterResponse: [responseBody, throwForStatus],
    csrfTokenKey: 'transfer-csrf',
    ...options,
  });

  const fetch = async <TBody = unknown>(
    url: string,
    init?: FetchInit & {
      params?: Params;
    }
  ) => {
    const { params, ...passedInit } = init || {};
    return (
      // TODO: Convert `addParams` to middleware after https://github.com/zapier/foundations-frontend/issues/42
      zapierFetch<TBody>(addParams(url, params), passedInit)
        .catch(createErrorHandler(url))
        .then(({ body }) => body)
    );
  };

  return {
    fetch,
    /** Submits a batch of records for processing. */
    submitBatch: submitBatch.bind(null, fetch),
    /** Logs a user out. */
    logout: logout.bind(null, fetch),
    /** Fetches the session of the currently logged-in user. */
    getSession: getSession.bind(null, fetch),
    /** Fetches the entitlement restrictions and duration for the proposed transfer. */
    getEntitlements: getEntitlementsImplementation.bind(null, fetch),
    /** Fetches records from the read step of a transfer zap. */
    getZapReadRecords: getZapReadRecords.bind(null, wsClient.bind(options!)),
    /** Fetches records and whether they match a set of FilterAPI filters. */
    getFilteredRecords: getFilteredRecords.bind(null, wsClient.bind(options!)),
    /** Fetches public trigger apps */
    getPublicTriggerApps: getPublicTriggerApps.bind(null, fetch),
    /** Fetches private trigger apps */
    getPrivateTriggerApps: getPrivateTriggerApps.bind(null, fetch),
    /** Fetches the schedule and filter settings associated with a scheduled transfer. */
    getSchedule: getSchedule.bind(null, fetch),
    /** Creates (or updates if one already exists) the schedule and filter associated with a zap. */
    createOrUpdateSchedule: createOrUpdateSchedule.bind(null, fetch),
  };
};

export default createClient;
